// ─────────────────────────────────────────────────────────────────────────────
// THE SIGNING PAGE. PUBLIC BY NECESSITY — the signer is not a Doorly user.
//
// 🚨 THE DOCUMENT IS NOT ON THIS PAGE UNTIL THE CHECK PASSES. The first design
// rendered the authorization on arrival and asked for the date of birth at the
// signature. That page names the borrower and their mortgage broker, and the
// address it was mailed to came from the BROKER about somebody who is not yet
// Doorly's customer — so one typo would have told a stranger that a named person
// is applying for a mortgage through a named broker. The server will not send
// the text before /verify succeeds; this page could not show it early even if it
// tried, and it is built in the same order so nobody later "simplifies" it back.
//
// ⚠️ THE REFUSAL SAYS ALMOST NOTHING, AND THAT IS NOT AN OVERSIGHT. "The date of
// birth does not match" tells a link holder they have the right person and the
// wrong date. The attempts remaining are shown because a borrower mistyping
// needs to know the ceiling exists; which field was wrong is not shown.
// ─────────────────────────────────────────────────────────────────────────────
import React, { useEffect, useState } from 'react';
import ThirdPartyLogo from '../components/ThirdPartyLogo';
import { BRAND_FONT_STACK } from '../constants/thirdPartyPages';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const PROMPT = {
  date_of_birth: {
    label: 'Your date of birth',
    hint: 'So we know it is you. Month, day and year.',
    type: 'date',
    placeholder: '',
  },
  ssn_last4: {
    label: 'The last four digits of your Social Security number',
    hint: 'So we know it is you. Four digits only.',
    type: 'text',
    placeholder: '1234',
  },
};

// 🔑 THE SERVER'S CODES, TRANSLATED ONCE. A page that prints a raw code teaches
// a borrower to email support with a word nobody outside the codebase knows.
const SAYS = {
  // 🚨 NAMES NOBODY (Jesse, 2026-10-28): "whoever sent it", never a person, role or firm.
  invalid_token: 'This link is not valid. Please ask whoever sent it to send it again.',
  token_expired: 'This link has expired. Please ask whoever sent it to send a new one.',
  already_signed: 'This authorization has already been signed. Nothing further is needed.',
  too_many_attempts:
    'For your protection we have stopped accepting attempts on this link. Please ask '
    + 'whoever sent it to send a new one.',
  verification_failed: 'That did not match what we have on file.',
  no_verifiable_field_on_file:
    'We cannot complete this electronically. Please ask whoever sent you '
    + 'this link for the printable form instead.',
};

const PANEL = {
  background: '#fff', border: '1px solid #e5e7eb', borderRadius: 14,
  padding: '26px 28px', maxWidth: 720, margin: '0 auto 20px',
};

export default function BorrowerAuthorizationSign() {
  const token = new URLSearchParams(window.location.search).get('token') || '';

  const [stage, setStage] = useState('loading');   // loading | verify | review | done | dead
  const [method, setMethod] = useState(null);
  const [attempts, setAttempts] = useState(null);
  const [value, setValue] = useState('');
  const [text, setText] = useState('');
  const [typedName, setTypedName] = useState('');
  const [agreed, setAgreed] = useState(false);
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) { setStage('dead'); setMessage(SAYS.invalid_token); return; }
    fetch(`${API_URL}/api/broker-authorization/validate/${encodeURIComponent(token)}`)
      .then((r) => r.json().then((d) => ({ ok: r.ok, d })))
      .then(({ ok, d }) => {
        if (!ok || !d.valid) {
          setStage('dead');
          setMessage(SAYS[d && d.error] || 'This link cannot be used.');
          return;
        }
        setMethod(d.verificationMethod);
        setAttempts(d.attemptsRemaining);
        setStage('verify');
      })
      .catch(() => {
        setStage('dead');
        setMessage('We could not reach Doorly. Please try again in a moment.');
      });
  }, [token]);

  const verify = async (e) => {
    e.preventDefault();
    setBusy(true); setMessage('');
    try {
      const res = await fetch(`${API_URL}/api/broker-authorization/verify/${encodeURIComponent(token)}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ verification: value }),
      });
      const d = await res.json();
      if (!res.ok) {
        // ⚠️ A SPENT, EXPIRED OR LOCKED LINK IS TERMINAL; A MISMATCH IS NOT.
        if (d.error === 'verification_failed') {
          setMessage(SAYS.verification_failed);
          if (typeof d.attemptsRemaining === 'number') setAttempts(d.attemptsRemaining);
          if (d.attemptsRemaining === 0) { setStage('dead'); setMessage(SAYS.too_many_attempts); }
        } else {
          setStage('dead');
          setMessage(SAYS[d.error] || 'This link cannot be used.');
        }
        return;
      }
      setText(d.text || '');
      setTypedName(d.signerName || '');
      setStage('review');
    } catch (_) {
      setMessage('We could not reach Doorly. Nothing was submitted — please try again.');
    } finally { setBusy(false); }
  };

  const sign = async (e) => {
    e.preventDefault();
    setBusy(true); setMessage('');
    try {
      const res = await fetch(`${API_URL}/api/broker-authorization/sign/${encodeURIComponent(token)}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ typedName }),
      });
      const d = await res.json();
      if (!res.ok) {
        setStage('dead');
        setMessage(SAYS[d.error] || 'We could not record your signature. Nothing was signed.');
        return;
      }
      setStage('done');
    } catch (_) {
      setMessage('We could not reach Doorly. Nothing was signed — please try again.');
    } finally { setBusy(false); }
  };

  const prompt = PROMPT[method] || PROMPT.date_of_birth;

  return (
    <div style={{ minHeight: '100vh', background: '#f5f6fc', padding: '40px 20px',
      fontFamily: BRAND_FONT_STACK }}>
      <div style={{ maxWidth: 720, margin: '0 auto 22px', textAlign: 'center' }}>
        <ThirdPartyLogo height={36} />
        <div style={{ fontSize: 13, color: '#6b7280', marginTop: 4 }}>Go Doorly, LLC · NMLS #2764749</div>
      </div>

      {stage === 'loading' && (
        <div style={{ ...PANEL, textAlign: 'center', color: '#6b7280', fontSize: 14 }}>Checking your link…</div>
      )}

      {stage === 'dead' && (
        <div style={PANEL}>
          <h1 style={{ fontSize: 18, fontWeight: 800, color: '#111827', margin: '0 0 10px' }}>
            This link cannot be used
          </h1>
          <p style={{ fontSize: 14, color: '#4b5563', lineHeight: 1.7, margin: 0 }}>{message}</p>
        </div>
      )}

      {stage === 'verify' && (
        <form onSubmit={verify} style={PANEL}>
          <h1 style={{ fontSize: 18, fontWeight: 800, color: '#111827', margin: '0 0 8px' }}>
            Confirm it is you
          </h1>
          {/* 🔑 WHY, BEFORE WHAT. A page that opens by demanding a date of birth
              from somebody who did not expect the email reads as a phishing
              attempt — which is exactly what it would be if it were one. */}
          <p style={{ fontSize: 14, color: '#4b5563', lineHeight: 1.7, margin: '0 0 18px' }}>
            You have been asked to sign one authorization so we can review your loan
            application. Before we show it to you, please confirm one detail — it is how we know the
            link reached the right person.
          </p>
          <label style={{ display: 'block', fontSize: 13, fontWeight: 700, color: '#374151', marginBottom: 6 }}>
            {prompt.label}
          </label>
          <input
            type={prompt.type}
            value={value}
            placeholder={prompt.placeholder}
            onChange={(e) => setValue(e.target.value)}
            inputMode={method === 'ssn_last4' ? 'numeric' : undefined}
            maxLength={method === 'ssn_last4' ? 4 : undefined}
            style={{ width: '100%', height: 46, padding: '0 12px', borderRadius: 10,
              border: '1px solid #e5e7eb', fontSize: 15, boxSizing: 'border-box' }}
          />
          <div style={{ fontSize: 12.5, color: '#9ca3af', marginTop: 6 }}>{prompt.hint}</div>
          {message && (
            <div style={{ marginTop: 14, padding: '10px 14px', borderRadius: 10,
              background: '#fff0f3', border: '1px solid #ffb3bf', color: '#b91c1c',
              fontSize: 13, lineHeight: 1.6 }}>
              {message}
              {typeof attempts === 'number' && attempts > 0
                ? ` You have ${attempts} attempt${attempts === 1 ? '' : 's'} left.`
                : ''}
            </div>
          )}
          <button type="submit" disabled={busy || !String(value).trim()}
            style={{ marginTop: 18, width: '100%', height: 48, borderRadius: 10, border: 'none',
              background: '#3a6ad9', color: '#fff', fontSize: 15, fontWeight: 700,
              cursor: busy ? 'wait' : 'pointer', opacity: String(value).trim() ? 1 : 0.5 }}>
            {busy ? 'Checking…' : 'Continue'}
          </button>
        </form>
      )}

      {stage === 'review' && (
        <form onSubmit={sign} style={PANEL}>
          <h1 style={{ fontSize: 18, fontWeight: 800, color: '#111827', margin: '0 0 14px' }}>
            Please read and sign
          </h1>
          {/* ⚠️ THE WHOLE TEXT, SCROLLABLE, NOT A SUMMARY WITH A LINK. What the
              signer saw is hashed and stored, and the hash has to attest to a
              document they could actually read on this page. */}
          <pre style={{ whiteSpace: 'pre-wrap', fontFamily: BRAND_FONT_STACK, fontSize: 13.5,
            lineHeight: 1.75, color: '#1f2937', background: '#fbfcfe',
            border: '1px solid #e5e7eb', borderRadius: 10, padding: '18px 20px',
            maxHeight: 420, overflowY: 'auto', margin: '0 0 18px' }}>{text}</pre>

          <label style={{ display: 'block', fontSize: 13, fontWeight: 700, color: '#374151', marginBottom: 6 }}>
            Type your full name to sign
          </label>
          <input
            type="text"
            value={typedName}
            onChange={(e) => setTypedName(e.target.value)}
            style={{ width: '100%', height: 46, padding: '0 12px', borderRadius: 10,
              border: '1px solid #e5e7eb', fontSize: 16, fontFamily: BRAND_FONT_STACK,
              boxSizing: 'border-box' }}
          />
          <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', marginTop: 14,
            fontSize: 13.5, color: '#4b5563', lineHeight: 1.6, cursor: 'pointer' }}>
            <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)}
              style={{ marginTop: 3, width: 17, height: 17, flexShrink: 0 }} />
            <span>
              I have read the authorization above and I am signing it electronically. I understand
              my electronic signature has the same effect as signing on paper.
            </span>
          </label>
          {message && (
            <div style={{ marginTop: 14, padding: '10px 14px', borderRadius: 10,
              background: '#fff0f3', border: '1px solid #ffb3bf', color: '#b91c1c',
              fontSize: 13, lineHeight: 1.6 }}>{message}</div>
          )}
          <button type="submit" disabled={busy || !agreed || !typedName.trim()}
            style={{ marginTop: 18, width: '100%', height: 48, borderRadius: 10, border: 'none',
              background: '#3a6ad9', color: '#fff', fontSize: 15, fontWeight: 700,
              cursor: busy ? 'wait' : 'pointer',
              opacity: agreed && typedName.trim() ? 1 : 0.5 }}>
            {busy ? 'Signing…' : 'Sign authorization'}
          </button>
          <p style={{ fontSize: 12.5, color: '#9ca3af', lineHeight: 1.6, margin: '12px 0 0' }}>
            Signing this does not obligate you to accept a loan. We will email you a copy.
          </p>
        </form>
      )}

      {stage === 'done' && (
        <div style={PANEL}>
          <h1 style={{ fontSize: 18, fontWeight: 800, color: '#111827', margin: '0 0 10px' }}>
            Signed — thank you
          </h1>
          <p style={{ fontSize: 14, color: '#4b5563', lineHeight: 1.7, margin: 0 }}>
            We have emailed you a copy of what you signed. Whoever sent you this link has been notified
            and nothing further is needed from you right now.
          </p>
        </div>
      )}
    </div>
  );
}
